import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { CheckCircle2, Mail, Shield, KeyRound } from "lucide-react";
import { motion } from "framer-motion";
import { useVerifyEmail } from "@/hooks/useAuth";
import { useEffect } from "react";
import PrefLogo from "@/assets/logo_municipal.svg";

export const VerifyEmail = () => {
  const navigate = useNavigate();
  const verifyEmail = useVerifyEmail();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const submitVerifyEmail = async () => {
    if (token) await verifyEmail.mutateAsync({ token });
  };

  useEffect(() => {
    submitVerifyEmail();
  }, []);

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-[hsl(var(--primary))]/20 via-[hsl(var(--primary))]/5 to-[hsl(var(--secondary))]/10">
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -top-24 -left-24 w-96 h-96 bg-[hsl(var(--primary))]/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, -90, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -bottom-24 -right-24 w-96 h-96 bg-[hsl(var(--secondary))]/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            y: [0, -20, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-1/2 left-1/4 w-64 h-64 bg-[hsl(var(--primary))]/5 rounded-full blur-2xl"
        />
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:20px_20px]" />
        </div>
      </div>

      {/* Container principal com duas colunas */}
      <div className="w-full max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Coluna esquerda - Informações */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden md:block"
        >
          <div className="space-y-8">
            <div>
              <div className="mb-2">
                <p className="text-sm font-semibold text-[hsl(var(--primary))] uppercase tracking-wider mb-2">
                  
                </p>
                <h1 className="text-6xl font-black mb-2 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] bg-clip-text text-transparent">
                  Vida+Simples
                </h1>
                <p className="text-lg font-medium text-gray-700">Ceará</p>
              </div>
              <p className="text-xl text-gray-600 leading-relaxed mt-6">
                {token
                  ? "Sua conta foi verificada com sucesso!"
                  : "Verifique seu e-mail para ativar sua conta"}
              </p>
            </div>

            {/* Cards de features */}
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <Shield className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Proteção de Dados
                  </h3>
                  <p className="text-sm text-gray-600">
                    Suas informações são protegidas com criptografia
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <KeyRound className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Verificação Segura
                  </h3>
                  <p className="text-sm text-gray-600">
                    Confirmação de e-mail para garantir a segurança da conta
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-[hsl(var(--primary))]/20"
              >
                <div className="p-3 bg-[hsl(var(--primary))]/10 rounded-lg">
                  <Mail className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Acesso Rápido
                  </h3>
                  <p className="text-sm text-gray-600">
                    Após verificação, acesse todos os recursos da plataforma
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Coluna direita - Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full"
        >
          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="space-y-4 pb-8">
              <div className="flex items-center justify-center mb-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                >
                  <img
                    src={PrefLogo}
                    alt="Logo Municipal"
                    className="h-25 w-25"
                  />
                </motion.div>
              </div>
              {token ? (
                <>
                  <div className="flex justify-center mb-2">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-10 w-10 text-green-600" />
                    </div>
                  </div>
                  <CardTitle className="text-3xl font-bold text-center text-gray-900">
                    E-mail Verificado!
                  </CardTitle>
                  <p className="text-center text-muted-foreground text-sm">
                    Sua conta foi ativada com sucesso
                  </p>
                </>
              ) : (
                <>
                  <div className="flex justify-center mb-2">
                    <div className="w-16 h-16 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center">
                      <Mail className="h-10 w-10 text-[hsl(var(--primary))]" />
                    </div>
                  </div>
                  <CardTitle className="text-3xl font-bold text-center text-gray-900">
                    Verifique seu E-mail
                  </CardTitle>
                  <p className="text-center text-muted-foreground text-sm">
                    Enviamos um link de verificação para seu e-mail
                  </p>
                </>
              )}
            </CardHeader>

            <CardContent>
              {token ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800 text-center">
                      Sua conta foi ativada com sucesso! Agora você pode fazer
                      login.
                    </p>
                  </div>

                  <Button
                    onClick={() => navigate("/login")}
                    className="w-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] hover:from-[hsl(var(--secondary))] hover:to-[hsl(var(--primary))] shadow-lg"
                  >
                    Ir para Login
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-[hsl(var(--primary))]/5 rounded-lg border border-[hsl(var(--primary))]/20">
                    <p className="text-sm text-gray-700 text-center">
                      Verifique sua caixa de entrada e spam. Clique no link
                      recebido para ativar sua conta.
                    </p>
                  </div>

                  <div className="text-center pt-2">
                    <Link
                      to="/login"
                      className="text-sm text-[hsl(var(--primary))] font-semibold hover:underline"
                    >
                      Voltar para Login
                    </Link>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
